<template>
  <ModalSmall @close="emit('close')">
    <div class="language-modal-wrapper">
      <section class="container-onboarding-content">
        <div class="wrapper-title">
          <h3>
            <AnimatedGradientText>{{ languageLabel }}</AnimatedGradientText>
          </h3>
          <h4 class="medium">
            <AnimationTyping :text="t(subtitleKey)" />
          </h4>
        </div>

        <SingleLanguageList :omit="disabledCode" :selected-language @select="selectLanguage" />
      </section>

      <div class="onboarding-footer">
        <ButtonProcess full-width @click="confirmLanguage" :disabled="isDisabled">
          {{ t('label_select_language', { language: languageLabel }) }}
        </ButtonProcess>
      </div>
    </div>
  </ModalSmall>
</template>

<script setup lang="ts">
import AnimationTyping from '@/components/animations/AnimationTyping.vue';
import SingleLanguageList from '@/components/domains/chat/chat-single/SingleLanguageList.vue';
import AnimatedGradientText from '@/components/shared/base/AnimatedGradientText.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import ModalSmall from '@/components/shared/composite/ModalSmall.vue';
import { useAudioStore } from '@/stores/audio';
import { useUserStore } from '@/stores/user';
import { Language } from '@/types/common';

const userStore = useUserStore();

const props = withDefaults(
  defineProps<{
    usingCustomSelect?: boolean;
    disabledCode?: string;
    subtitleKey?: string;
    usingDefaultCode?: boolean;
    currentLanguage: Language | null;
  }>(),
  {
    usingCustomSelect: false,
    disabledCode: '',
    subtitleKey: 'please_select_language',
    usingDefaultCode: false,
  }
);
const emit = defineEmits<{ close: []; select: [language: Language] }>();

const { t } = useI18n();

const audioStore = useAudioStore();

const selectedLanguage = ref<Language | null>(null);

const languageLabel = computed(() => {
  const language = selectedLanguage.value ? selectedLanguage.value.code : userStore.userInfo.languageCode;
  return t(`label_language_${language}`);
});

const isDisabled = computed(() => {
  return !selectedLanguage.value;
});

const selectLanguage = (language: Language) => {
  selectedLanguage.value = language;
};

const confirmLanguage = () => {
  if (!selectedLanguage.value) return;

  if (props.usingCustomSelect) {
    emit('select', selectedLanguage.value);
  } else {
    userStore.setPageLanguage(selectedLanguage.value.code);
  }

  emit('close');
};

onMounted(() => {
  if (props.currentLanguage && props.disabledCode !== props.currentLanguage?.code) {
    selectedLanguage.value = props.currentLanguage;
  }
});
</script>

<style scoped lang="scss">
.language-modal-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: flex-end;
}

.container-onboarding-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 1rem;
}

.rotated {
  transform: rotate(180deg);
}

.wrapper-title {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.onboarding-footer {
  display: flex;
  gap: 0.5rem;
}

.btn-prev {
  width: 30%;
  max-width: 8rem;
}

.btn-full {
  flex: 1;
}
</style>
